import React, { forwardRef } from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      className,
      type = 'text',
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [inputType, setInputType] = React.useState(type);

    React.useEffect(() => {
      if (showPasswordToggle && type === 'password') {
        setInputType(showPassword ? 'text' : 'password');
      }
    }, [showPassword, showPasswordToggle, type]);

    const baseInputClasses =
      'w-full rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2';

    const inputClasses = clsx(
      baseInputClasses,
      {
        // Padding adjustments for icons
        'pl-10': leftIcon,
        'pr-10': rightIcon || showPasswordToggle,
        'pl-10 pr-10': leftIcon && (rightIcon || showPasswordToggle),
        'px-3': !leftIcon && !rightIcon && !showPasswordToggle,
      },
      'py-2 text-base',
      // States
      error
        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500',
      props.disabled && 'bg-gray-100 cursor-not-allowed',
      className
    );

    const togglePassword = () => {
      setShowPassword(!showPassword);
    };

    return (
      <div className="space-y-1">
        {label && (
          <label
            htmlFor={props.id || props.name}
            className="block text-sm font-medium text-gray-700"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-400">{leftIcon}</span>
            </div>
          )}

          <input ref={ref} type={inputType} className={inputClasses} {...props} />

          {(rightIcon || showPasswordToggle) && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {showPasswordToggle && type === 'password' ? (
                <button
                  type="button"
                  onClick={togglePassword}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              ) : (
                <span className="text-gray-400 pointer-events-none">{rightIcon}</span>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-1 text-red-600 text-sm">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        {helperText && !error && <p className="text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
